import { isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { filter, map, merge, switchMap } from 'rxjs';
import { GROWTHBOOK } from '../../growthbook';
import { sleep } from '../../lib/sleep';
import { PICFLOW_CONFIG } from '../../picflow-config';
import { PICFLOW_STORYBLOK } from '../../picflow-storyblok';
import { SafeHtmlPipe } from '../../pipes/safe-html.pipe';
import { PictureComponent } from '../../shared/components/picture/picture.component';
import { SvgComponent } from '../../shared/components/svg/svg.component';
import { WebsiteFooterComponent } from '../../shared/components/website-footer/website-footer.component';
import { WebsiteHeaderComponent } from '../../shared/components/website-header/website-header.component';
import { ButtonDirective } from '../../shared/directives/button.directive';
import { ArticleBreadcrumbComponent } from '../components/article-breadcrumb/article-breadcrumb.component';
import { ArticleShareComponent } from '../components/article-share/article-share.component';
import { ArticleSpeechComponent } from '../components/article-speech/article-speech.component';
import { BlockStyle } from '../components/block-wrapper/block-wrapper.component';
import { BlogPostLinkListComponent } from '../components/blog-post-link-list/blog-post-link-list.component';
import { NewBlogMetaComponent } from '../components/new-blog-meta/new-blog-meta.component';
import { PreFooterComponent } from '../components/pre-footer/pre-footer.component';
import { ScrollProgressIndicatorComponent } from '../components/scroll-progress-indicator/scroll-progress-indicator.component';
import { TableOfContentsComponent } from '../components/table-of-contents/table-of-contents.component';
import { TextComponent } from '../components/text/text.component';
import { StoryblokCdnPipe } from '../pipes/storyblok-cdn.pipe';
import { StoryblokDirective } from '../storyblok.directive';

@Component({
  selector: 'app-storyblok-article',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './storyblok-article.component.html',
  styleUrls: ['./storyblok-article.component.scss'],
  imports: [
    StoryblokDirective,
    WebsiteHeaderComponent,
    PreFooterComponent,
    RouterLink,
    ButtonDirective,
    WebsiteFooterComponent,
    ScrollProgressIndicatorComponent,
    TextComponent,
    SafeHtmlPipe,
    StoryblokCdnPipe,
    PictureComponent,
    TableOfContentsComponent,
    NewBlogMetaComponent,
    ArticleBreadcrumbComponent,
    BlogPostLinkListComponent,
    ArticleSpeechComponent,
    SvgComponent,
    ArticleShareComponent,
  ],
})
export class StoryblokArticleComponent implements OnInit {
  public growthBook = inject(GROWTHBOOK);
  private platformId = inject(PLATFORM_ID);
  private config = inject(PICFLOW_CONFIG);
  private route = inject(ActivatedRoute);
  private storyblokService = inject(PICFLOW_STORYBLOK);
  @ViewChild('videoEl', { static: false }) videoEl: ElementRef<HTMLVideoElement>;

  story = toSignal(
    merge(this.route.data, this.storyblokService.updates$.pipe(filter(Boolean))).pipe(map(data => data?.story))
  );

  visual = computed(() => {
    const story = this.story();
    const visual = story?.content.visual;
    return visual;
  });

  dimensions = computed(() => {
    const asset = this.visual();
    if (!asset) return { width: 0, height: 0 };
    const url = asset.filename;
    return {
      width: parseInt(url.split('/')[5].split('x')[0]),
      height: parseInt(url.split('/')[5].split('x')[1]),
    };
  });

  readonly gptLink = computed(() => {
    const story = this.story();
    const thisUrl = `${this.config.correctPicflowUrl}/${story.full_slug}`;
    const text = `Read from this Picflow article so I can ask questions about it: ${thisUrl}`;
    return `https://chatgpt.com/?hints=search&q=${encodeURIComponent(text)}`;
  });

  contentStyle = [<BlockStyle>{ topSpacing: '0', bottomSpacing: '0', pageWidth: '0-5' }];
  relatedPostsStyle = [<BlockStyle>{ topSpacing: '7', bottomSpacing: '0', pageWidth: 'default' }];

  private unfilteredRelatedPosts = toSignal(
    toObservable(this.story).pipe(
      switchMap(story => {
        if (!story?.content?.topic) return [];
        return this.storyblokService.getStories({
          content_type: 'Article',
          per_page: 4,
          sort_by: 'first_published_at:desc',
          filter_query: [{ key: 'topic', value: story.content.topic }],
        });
      }),
      map(resp => resp.stories)
    )
  );

  relatedPosts = computed(() => {
    const relatedPosts = this.unfilteredRelatedPosts();
    const story = this.story();
    if (!relatedPosts || relatedPosts.length === 0 || !story) return [];
    return relatedPosts.filter(post => post.uuid !== story.uuid).slice(0, 3);
  });

  async ngOnInit() {
    const visual = this.visual();
    if (!visual) return;
    if (isPlatformServer(this.platformId)) return;
    await sleep(0);
    if (!this.videoEl) return;

    this.videoEl.nativeElement.muted = true;
    this.videoEl.nativeElement.autoplay = true;
  }
}
